<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <Toast />

        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h1 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Продажи флориста</h1>
            <p class="mt-1 text-sm text-slate-500 md:text-base">
              {{ pageSubtitle }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              label="Назад"
              icon="pi pi-arrow-left"
              severity="secondary"
              outlined
              class="!border-slate-200 !shadow-sm"
              @click="router.push('/dashboard')"
            />
            <Button
              label="Обновить"
              icon="pi pi-refresh"
              severity="success"
              class="!shadow-sm"
              :loading="loading"
              @click="fetchSalesData"
            />
          </div>
        </div>

        <section class="mb-4 grid shrink-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div class="mb-2 flex items-center justify-between">
              <i class="pi pi-shopping-bag text-lg text-emerald-700"></i>
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">Все продажи</span>
            </div>
            <p class="text-[28px] font-semibold leading-none text-slate-900">{{ totalSalesCount.toLocaleString('ru-RU') }}</p>
            <p class="mt-2 text-sm text-slate-500">Количество чеков</p>
          </article>

          <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div class="mb-2 flex items-center justify-between">
              <i class="pi pi-wallet text-lg text-emerald-700"></i>
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">К оплате</span>
            </div>
            <p class="text-[28px] font-semibold leading-none text-slate-900">{{ formatCurrency(totalSalesAmount) }}</p>
            <p class="mt-2 text-sm text-slate-500">Сумма продаж</p>
          </article>

          <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div class="mb-2 flex items-center justify-between">
              <i class="pi pi-box text-lg text-emerald-700"></i>
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">Позиции</span>
            </div>
            <p class="text-[28px] font-semibold leading-none text-slate-900">{{ totalItemsCount.toLocaleString('ru-RU') }}</p>
            <p class="mt-2 text-sm text-slate-500">Продано товаров</p>
          </article>

          <article class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div class="mb-2 flex items-center justify-between">
              <i class="pi pi-chart-line text-lg text-emerald-700"></i>
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">Средний чек</span>
            </div>
            <p class="text-[28px] font-semibold leading-none text-slate-900">{{ formatCurrency(averageCheckAmount) }}</p>
            <p class="mt-2 text-sm text-slate-500">Средняя сумма продажи</p>
          </article>
        </section>

        <section class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
          <div class="mb-4 flex shrink-0 flex-col gap-3">
            <div class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div class="flex w-full max-w-2xl flex-1 gap-2">
                <IconField class="w-full">
                  <InputIcon><i class="pi pi-search" /></InputIcon>
                  <InputText
                    v-model.trim="searchQuery"
                    class="w-full"
                    placeholder="Поиск по номеру продажи, клиенту, телефону или счёту..."
                  />
                </IconField>
              </div>

              <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                Найдено продаж: <span class="font-semibold text-slate-900">{{ filteredSales.length }}</span>
              </div>
            </div>

            <div class="flex flex-col gap-2 lg:flex-row lg:items-center">
              <Select
                v-model="selectedClientTypeId"
                :options="clientTypeFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full lg:max-w-xs"
              />

              <InputText
                v-model="dateRange.from"
                type="date"
                class="w-full lg:max-w-[11rem]"
              />

              <InputText
                v-model="dateRange.to"
                type="date"
                class="w-full lg:max-w-[11rem]"
              />
            </div>
          </div>

          <div v-if="loading" class="flex min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
            Загрузка продаж...
          </div>

          <div v-else-if="!filteredSales.length" class="flex min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
            Продажи не найдены
          </div>

          <div v-else class="min-h-0 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div class="min-w-max">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <thead class="bg-slate-50">
                  <tr>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Продажа</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Дата</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Клиент</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Тип клиента</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Телефон</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Счёт</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Позиций</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Сумма</th>
                    <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-right font-semibold text-slate-600">Действие</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr
                    v-for="sale in filteredSales"
                    :key="sale.id"
                    class="transition-colors hover:bg-emerald-50/35"
                  >
                    <td class="px-4 py-3">
                      <div class="min-w-0">
                        <p class="font-semibold text-slate-900">{{ sale.sale_id }}</p>
                        <p class="mt-1 text-xs text-slate-500">{{ sale.employee || 'Продавец не указан' }}</p>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-slate-600">{{ formatDateTime(sale.date) }}</td>
                    <td class="px-4 py-3 text-slate-600">{{ sale.client || 'Без клиента' }}</td>
                    <td class="px-4 py-3 text-slate-600">{{ sale.client_type || '—' }}</td>
                    <td class="px-4 py-3 text-slate-600">{{ formatPhone(sale.client_phone) || 'Телефон не указан' }}</td>
                    <td class="px-4 py-3 text-slate-600">
                      <div class="min-w-0">
                        <p class="truncate font-medium text-slate-900">{{ sale.account_name || 'Счёт' }}</p>
                        <p class="truncate text-xs text-slate-500">{{ sale.account || 'Не указан' }}</p>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-slate-600">{{ Number(sale.items_quantity || 0).toLocaleString('ru-RU') }}</td>
                    <td class="px-4 py-3">
                      <p class="font-semibold text-slate-900">{{ formatCurrency(sale.price_after_discount) }}</p>
                      <p class="mt-1 text-xs text-slate-500">До скидки: {{ formatCurrency(sale.total_amount) }}</p>
                    </td>
                    <td class="px-4 py-3 text-right">
                      <Button
                        label="Подробнее"
                        icon="pi pi-eye"
                        size="small"
                        severity="secondary"
                        outlined
                        @click="openSaleDetails(sale)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="showSaleDetailsDialog"
    modal
    header="Детали продажи"
    :style="{ width: '1040px', maxWidth: '96vw' }"
  >
    <div v-if="saleDetailsLoading" class="py-6 text-base text-gray-500">Загрузка деталей...</div>
    <div v-else-if="!selectedSaleDetails" class="py-6 text-base text-gray-500">Данные по продаже не найдены</div>

    <div v-else class="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <section class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <h3 class="mb-3 text-lg font-semibold text-slate-900">Продажа</h3>
        <div class="space-y-3 text-sm">
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Номер</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Дата</p>
            <p class="mt-1 font-semibold text-slate-900">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Клиент</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.client?.name || 'Без клиента' }}</p>
            <p class="mt-1 text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone) || 'Телефон не указан' }}</p>
            <p class="mt-1 text-slate-500">{{ selectedSaleDetails.client?.client_type || 'Тип не указан' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Продавец</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Счёт</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</p>
          </div>
        </div>
      </section>

      <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 md:p-5">
          <div class="border-b border-dashed border-slate-300 pb-4 text-center">
            <p class="text-xs font-medium uppercase tracking-[0.22em] text-slate-500">Чек продажи</p>
            <p class="mt-2 text-2xl font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
            <p class="mt-1 text-sm text-slate-500">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>

          <div class="mt-4 space-y-2 border-b border-dashed border-slate-300 pb-4 text-sm text-slate-600">
            <div class="flex items-start justify-between gap-3">
              <span>Клиент</span>
              <div class="text-right">
                <p class="font-medium text-slate-900">{{ selectedSaleDetails.client?.name || 'Без клиента' }}</p>
                <p class="text-xs text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone) || 'Телефон не указан' }}</p>
              </div>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Счёт</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</span>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Продавец</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</span>
            </div>
          </div>

          <div class="mt-4 space-y-3">
            <article
              v-for="item in saleReceiptItems"
              :key="item.id"
              class="rounded-2xl border border-slate-200 bg-slate-50 p-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-base font-semibold text-slate-900">{{ item.label }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.meta }}</p>
                </div>
                <div class="shrink-0 text-right">
                  <p class="text-base font-semibold text-slate-900">{{ formatCurrency(item.total_price) }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.quantityLabel }}</p>
                </div>
              </div>

              <div v-if="item.showComposition" class="mt-3">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                  @click="toggleSaleItemDetails(item.id)"
                >
                  <span class="font-medium">
                    Состав позиции
                    <span class="text-slate-500">({{ item.components.length }})</span>
                  </span>
                  <i
                    class="pi text-xs text-slate-500"
                    :class="isSaleItemExpanded(item.id) ? 'pi-chevron-up' : 'pi-chevron-down'"
                  />
                </button>

                <div
                  v-if="isSaleItemExpanded(item.id)"
                  class="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white p-3"
                >
                  <div
                    v-for="component in item.components"
                    :key="`${item.id}-${component.product_id}-${component.barcode}`"
                    class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
                  >
                    <div class="flex items-start justify-between gap-3 text-sm">
                      <div class="min-w-0">
                        <p class="truncate font-medium text-slate-900">{{ component.name }}</p>
                        <p class="truncate text-xs text-slate-500">{{ component.barcode || 'Штрихкод не указан' }}</p>
                        <p class="mt-1 truncate text-xs text-slate-400">
                          {{ component.classification || 'Без класса' }} · {{ component.category || 'Без категории' }}
                        </p>
                      </div>
                      <div class="shrink-0 text-right text-xs text-slate-600">
                        <p>{{ Number(component.quantity || 0) }} × {{ formatCurrency(component.unit_price) }}</p>
                        <p class="mt-1 font-semibold text-slate-900">{{ formatCurrency(component.total_price) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div class="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div class="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Сумма по чеку</span>
              <span class="font-medium text-slate-900">{{ formatCurrency(selectedSaleDetails.total_amount) }}</span>
            </div>
            <div v-if="saleDiscountAmount > 0" class="mt-2 flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Скидка / бонусы</span>
              <span class="font-medium text-emerald-700">-{{ formatCurrency(saleDiscountAmount) }}</span>
            </div>
            <div class="mt-3 flex items-center justify-between gap-3 border-t border-dashed border-emerald-200 pt-3">
              <span class="text-base font-semibold text-slate-900">К оплате</span>
              <span class="text-2xl font-semibold text-slate-900">{{ formatCurrency(selectedSaleDetails.price_after_discount) }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <Button label="Закрыть" severity="secondary" outlined @click="showSaleDetailsDialog = false" />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../lib/axios'
import Topbar from './Topbar.vue'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

const router = useRouter()
const toast = useToast()
const loading = ref(false)
const employee = ref(null)
const sales = ref([])
const clientTypes = ref([])
const searchQuery = ref('')
const saleDetailsLoading = ref(false)
const selectedSaleDetails = ref(null)
const showSaleDetailsDialog = ref(false)
const expandedSaleItemIds = ref([])
const selectedClientTypeId = ref(null)
const dateRange = ref({
  from: '',
  to: '',
})
const almatyTimeZone = 'Asia/Almaty'
const SINGLE_PRODUCT_ORDER_TYPE_ID = 8
const EXTRA_WORK_ORDER_TYPE_ID = 9
let autoFetchTimer = null

const employeeName = computed(() => employee.value?.name || '')
const clientTypeFilterOptions = computed(() => ([
  { label: 'Все типы клиентов', value: null },
  ...clientTypes.value.map((item) => ({
    label: item.name,
    value: item.id,
  })),
]))

const pageSubtitle = computed(() => {
  if (employeeName.value) {
    return `Все продажи, оформленные сотрудником ${employeeName.value}`
  }

  return 'Сотрудник не определён, показаны доступные продажи'
})

const filteredSales = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) {
    return sales.value
  }

  return sales.value.filter((sale) => {
    const haystack = [
      sale.sale_id,
      sale.client,
      sale.client_type,
      formatPhone(sale.client_phone),
      sale.client_phone,
      sale.account_name,
      sale.account,
      sale.employee,
    ].map((value) => String(value || '').toLowerCase())

    return haystack.some((value) => value.includes(query))
  })
})

const totalSalesCount = computed(() => sales.value.length)
const totalSalesAmount = computed(() => sales.value.reduce((sum, sale) => sum + Number(sale.price_after_discount || 0), 0))
const totalItemsCount = computed(() => sales.value.reduce((sum, sale) => sum + Number(sale.items_quantity || 0), 0))
const averageCheckAmount = computed(() => {
  if (!totalSalesCount.value) return 0
  return Math.round(totalSalesAmount.value / totalSalesCount.value)
})

const hasValidDateRange = () => {
  if (!dateRange.value.from || !dateRange.value.to) {
    return true
  }

  return dateRange.value.from <= dateRange.value.to
}

const scheduleFetchSalesData = () => {
  if (!hasValidDateRange()) {
    return
  }

  if (autoFetchTimer) {
    clearTimeout(autoFetchTimer)
  }

  autoFetchTimer = window.setTimeout(() => {
    fetchSalesData()
  }, 250)
}

const saleReceiptItems = computed(() => {
  const items = selectedSaleDetails.value?.items || []

  return items.map((item, index) => {
    const components = Array.isArray(item.components) ? item.components : []
    const isSingleProduct = Number(item.order_type_id || 0) === SINGLE_PRODUCT_ORDER_TYPE_ID
    const componentsTotal = components.reduce((sum, component) => sum + Number(component.total_price || 0), 0)
    const totalPrice = Number(item.total_price || 0)
    const extraCharge = Math.max(0, totalPrice - componentsTotal)
    const componentQuantity = components.reduce((sum, component) => sum + Number(component.quantity || 0), 0)
    const displayQuantity = isSingleProduct && componentQuantity > 0
      ? componentQuantity
      : Number(item.quantity || 0) || 1
    const primaryName = item.title
      ? String(item.title).trim()
      : Number(item.order_type_id || 0) === EXTRA_WORK_ORDER_TYPE_ID
        ? 'Доп работа'
        : components.length === 1
        ? components[0]?.name
        : item.product_type || null

    return {
      ...item,
      label: primaryName || `Позиция ${index + 1}`,
      showComposition: !isSingleProduct && components.length > 0,
      meta: [
        item.product_type,
        item.product_level,
        components.length > 1 ? `${components.length} компонентов` : null,
      ].filter(Boolean).join(' · '),
      quantityLabel: `Кол-во: ${displayQuantity}`,
      components,
      componentsTotal,
      extraCharge,
      extraChargeLabel: components.length ? 'Сборка / наценка' : 'Доп. работа',
    }
  })
})

const saleDiscountAmount = computed(() => {
  const total = Number(selectedSaleDetails.value?.total_amount || 0)
  const payable = Number(selectedSaleDetails.value?.price_after_discount || 0)
  return Math.max(0, total - payable)
})

const getAlmatyDateParts = (value = new Date()) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: almatyTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const parts = formatter.formatToParts(value)
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]))

  return {
    year: map.year,
    month: map.month,
    day: map.day,
    hour: map.hour,
    minute: map.minute,
  }
}

const formatDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const { year, month, day, hour, minute } = getAlmatyDateParts(date)
  return `${year}-${month}-${day} ${hour}:${minute}`
}

const formatPhone = (value) => {
  const digits = String(value ?? '').replace(/\D+/g, '')
  if (!digits) return ''

  const normalized = digits.length === 10 ? `7${digits}` : digits
  if (normalized.length !== 11 || !normalized.startsWith('7')) {
    return String(value ?? '')
  }

  return `+${normalized[0]}(${normalized.slice(1, 4)})${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`
}

const formatCurrency = (value) => {
  return `${Number(value || 0).toLocaleString('ru-RU')} ₸`
}

const isSaleItemExpanded = (itemId) => expandedSaleItemIds.value.includes(itemId)

const toggleSaleItemDetails = (itemId) => {
  if (isSaleItemExpanded(itemId)) {
    expandedSaleItemIds.value = expandedSaleItemIds.value.filter((id) => id !== itemId)
    return
  }

  expandedSaleItemIds.value = [...expandedSaleItemIds.value, itemId]
}

const openSaleDetails = async (sale) => {
  if (!sale?.id) return

  showSaleDetailsDialog.value = true
  saleDetailsLoading.value = true
  expandedSaleItemIds.value = []

  try {
    const res = await axios.get(`/api/sale-items/${sale.id}`)
    selectedSaleDetails.value = res.data || null
    expandedSaleItemIds.value = (selectedSaleDetails.value?.items || [])
      .filter((item) => Number(item.order_type_id || 0) !== 8 && Array.isArray(item.components) && item.components.length > 0)
      .slice(0, 1)
      .map((item) => item.id)
  } catch (_error) {
    selectedSaleDetails.value = null
    expandedSaleItemIds.value = []
  } finally {
    saleDetailsLoading.value = false
  }
}

const fetchSalesData = async () => {
  if (!hasValidDateRange()) {
    return
  }

  loading.value = true

  try {
    const barcode = localStorage.getItem('barcode')
    const [employeeRes, clientTypesRes] = await Promise.all([
      axios.get('/api/get-employee', { params: { barcode } }).catch(() => ({ data: null })),
      axios.get('/api/client-types').catch(() => ({ data: [] })),
    ])
    employee.value = employeeRes.data || null
    clientTypes.value = Array.isArray(clientTypesRes.data) ? clientTypesRes.data : []

    const params = employee.value?.id ? { employee_id: employee.value.id } : {}
    if (selectedClientTypeId.value) {
      params.client_type_id = selectedClientTypeId.value
    }
    if (dateRange.value.from) {
      params.from = dateRange.value.from
    }
    if (dateRange.value.to) {
      params.to = dateRange.value.to
    }
    const salesRes = await axios.get('/api/sale', { params }).catch(() => ({ data: [] }))
    sales.value = Array.isArray(salesRes.data) ? salesRes.data : []
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error?.response?.data?.message || 'Не удалось загрузить продажи',
      life: 4000,
    })
  } finally {
    loading.value = false
  }
}

watch(selectedClientTypeId, () => {
  scheduleFetchSalesData()
})

watch(
  () => [dateRange.value.from, dateRange.value.to],
  ([from, to], [previousFrom, previousTo]) => {
    if (from === previousFrom && to === previousTo) {
      return
    }

    scheduleFetchSalesData()
  }
)

onMounted(fetchSalesData)

onBeforeUnmount(() => {
  if (autoFetchTimer) {
    clearTimeout(autoFetchTimer)
  }
})
</script>
